import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { Apple, ArrowDown, HeartHandshake, Stethoscope } from "lucide-react";
import { APP_STORE_URL, SHOTS } from "@/lib/site";

const lineVariant = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { type: "spring", stiffness: 80, damping: 19, delay: 0.25 + i * 0.14 },
  }),
};

const fadeVariant = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.75 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PhoneCard = () => (
  <div
    data-testid="hero-phone-mockup"
    className="relative w-[270px] rounded-[2.8rem] bg-ink p-[10px] shadow-[0_50px_100px_-30px_rgba(60,20,10,0.55)] sm:w-[290px]"
  >
    <div className="relative aspect-[9/19.5] overflow-hidden rounded-[2.2rem] bg-espresso">
      <img
        src={SHOTS.verified}
        alt="Verified STETH profile: Marcus, 30, ER Nurse"
        className="h-full w-full object-cover"
      />
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative overflow-hidden bg-[linear-gradient(135deg,#F7C6B6_0%,#E07A5F_48%,#C86A52_100%)] pt-[72px] text-white"
    >
      <div
        aria-hidden="true"
        className="absolute -left-48 -top-48 h-[36rem] w-[36rem] rounded-full bg-white/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-56 -right-32 h-[32rem] w-[32rem] rounded-full bg-terracotta-dark/40 blur-3xl"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-14 sm:px-8 sm:pb-24 sm:pt-20 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.span
            variants={fadeVariant}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2 text-xs font-bold text-white backdrop-blur-sm"
          >
            <Stethoscope size={14} /> For verified healthcare professionals
          </motion.span>

          <h1 className="mt-7 font-editorial text-[13vw] font-semibold leading-[1.04] tracking-[-0.01em] sm:text-6xl lg:text-7xl">
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineVariant} initial="hidden" animate="show" custom={0} className="block">
                Off call for work,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span variants={lineVariant} initial="hidden" animate="show" custom={1} className="block">
                on call for <span className="font-normal italic text-[#FFF1E8]">love.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeVariant}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg"
          >
            STETH is the dating app built for doctors, nurses, and allied health
            pros, people who get the shift, the schedule, and the calling
            behind the credentials.
          </motion.p>

          <motion.div
            variants={fadeVariant}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="app-store-download-btn-hero"
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 18 }}
              className="inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 font-bold text-terracotta-dark shadow-[0_20px_45px_-15px_rgba(120,40,20,0.55)]"
            >
              <Apple size={22} strokeWidth={2.4} />
              <span className="flex flex-col items-start leading-none">
                <span className="text-[10px] font-semibold uppercase tracking-widest opacity-70">
                  Download on the
                </span>
                <span className="text-base font-extrabold">App Store</span>
              </span>
            </motion.a>
            <a
              href="#how"
              data-testid="hero-how-it-works-link"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#how")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-2 rounded-full border-[1.5px] border-white/50 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
            >
              See how it works
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
          </motion.div>

          <motion.div
            variants={fadeVariant}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex items-center gap-2.5 text-xs font-semibold text-white/90"
          >
            <span className="inline-flex h-7 w-9 items-center justify-center rounded-lg bg-white/20 text-[11px] font-extrabold text-white">
              18+
            </span>
            You must be 18 or older to create an account.
          </motion.div>
        </div>

        <div className="relative flex justify-center lg:justify-end" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 90, rotate: 5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ type: "spring", stiffness: 55, damping: 16, delay: 0.55 }}
            style={{ rotateX, rotateY, y: phoneY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <PhoneCard />
            <motion.div
              data-testid="hero-match-card"
              style={{ transform: "translateZ(70px)" }}
              className="drift absolute -left-16 bottom-28 hidden items-center gap-3 rounded-2xl border border-white/40 bg-white/90 p-3.5 pr-5 shadow-[0_25px_50px_-20px_rgba(60,20,10,0.5)] backdrop-blur-md sm:flex"
            >
              <img src={SHOTS.firstdate} alt="" className="h-11 w-11 rounded-xl object-cover object-[50%_38%]" />
              <div>
                <div className="flex items-center gap-1 text-xs font-extrabold text-ink">
                  <HeartHandshake size={13} className="text-terracotta" /> It's a match
                </div>
                <div className="text-[11px] font-semibold text-clay">Ava, 29 · Cardiology</div>
              </div>
            </motion.div>
            <motion.div
              style={{ transform: "translateZ(50px)" }}
              className="drift-slow absolute -bottom-9 -right-5 hidden rounded-2xl border border-white/40 bg-white/90 px-4 py-3 shadow-[0_25px_50px_-20px_rgba(60,20,10,0.5)] backdrop-blur-md sm:block"
            >
              <svg width="86" height="30" viewBox="0 0 86 30" fill="none" aria-hidden="true">
                <path d="M2 15H18L24 4L32 26L38 15H52L56 9L60 15H84" stroke="#4E8B6C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ekg-run" />
                <path d="M2 15H18L24 4L32 26L38 15H52L56 9L60 15H84" stroke="#81B29A" strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-clay">
                Pulse check · mutual
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
